import { useEffect, useRef, useState } from 'preact/hooks';
import type { SupabaseClient } from '@supabase/supabase-js';
import { MODULOS, SECCION, useRuta } from './rutas';
import { Inicio } from './Inicio';
import { Productos } from './secciones/Productos';
import { ProductoDetalle } from './secciones/ProductoDetalle';
import { Precios } from './secciones/Precios';
import { Inventario } from './secciones/Inventario';
import { Gastos } from './secciones/Gastos';
import { Resultados } from './secciones/Resultados';
import { TableroOperacion } from './secciones/TableroOperacion';
import { Proximo } from './secciones/Proximo';
import { Pedidos } from './secciones/Pedidos';
import { PedidoForm } from './secciones/PedidoForm';
import { Compartir } from './secciones/Compartir';
import { Clientes } from './secciones/Clientes';
import { ClienteDetalle } from './secciones/ClienteDetalle';
import { Ajustes } from './secciones/Ajustes';
import { CampanaDetalle, Campanas } from './secciones/Campanas';
import { MensajeDetalle, MensajeNuevo } from './secciones/Mensajes';
import { cargarConfig, type Config } from './datos/pedidos';
import { PreguntaObjetivo, usePreguntaObjetivo } from './secciones/ObjetivoMes';
import { Todo } from './secciones/Todo';
import { alertasDeTodo } from './datos/todo';
import { addDias, hoy } from './logica/fechas';

// Con la sesión completa (contraseña + código): revisa que el correo esté en el equipo y muestra el menú.
export function Sistema({ sb }: { sb: SupabaseClient }) {
  const [nombre, setNombre] = useState<string | null | undefined>(undefined);
  const [error, setError] = useState('');
  const [ruta, ir] = useRuta();
  // Primer ingreso del mes: se pregunta el objetivo de ventas (una vez por mes, para todo el equipo).
  const objetivo = usePreguntaObjetivo(sb, typeof nombre === 'string');
  // Seguimientos del bloc de To Do's que llegaron (hoy o antes): el número que se ve junto a "To Do's" en el menú.
  const [todoN, setTodoN] = useState(0);
  const contarTodo = () => { const d = hoy(); alertasDeTodo(sb, d, addDias(d, 1)).then(r => setTodoN(r.alertas.length)); };
  useEffect(() => { if (typeof nombre === 'string') contarTodo(); }, [nombre, ruta.menu]);
  // Ajustes y catálogo para pedidos y clientes: se leen una vez por sesión, cuando se abre alguna de esas secciones.
  const [cfg, setCfg] = useState<Config | null>(null), [errCfg, setErrCfg] = useState('');
  const usaCfg = ['pedidos', 'clientes', 'productos', 'ajustes', 'campanas', 'tablero', 'gastos'].includes(ruta.menu);
  useEffect(() => { if (usaCfg && !cfg && nombre) cargarConfig(sb).then(setCfg).catch(e => setErrCfg(String(e.message || e))); }, [usaCfg, nombre]);
  // Tras un cambio en Ajustes se vuelve a leer la configuración (una vez, aunque se hagan varios cambios seguidos).
  const espera = useRef<ReturnType<typeof setTimeout>>();
  const recargarCfg = () => { clearTimeout(espera.current); espera.current = setTimeout(() => cargarConfig(sb).then(setCfg).catch(() => {}), 600); };
  useEffect(() => {
    (async () => {
      const { data: { user } } = await sb.auth.getUser();
      const { data, error } = await sb.from('equipo').select('nombre').eq('email', (user?.email || '').toLowerCase()).maybeSingle();
      if (error) setError('No se pudieron leer los datos: ' + error.message); else setNombre(data?.nombre || null);
    })();
  }, []);
  // En celular el menú es una fila que se desliza: se lleva a la vista la sección abierta (sin mover la página).
  useEffect(() => {
    const menu = document.querySelector<HTMLElement>('.menu'), on = menu?.querySelector<HTMLElement>('.nav.on');
    if (menu && on && menu.scrollWidth > menu.clientWidth) menu.scrollLeft += on.getBoundingClientRect().left - menu.getBoundingClientRect().left - (menu.clientWidth - on.offsetWidth) / 2;
  }, [ruta.menu, nombre]);
  if (error) return <section class="panel"><p class="error">{error}</p></section>;
  if (nombre === undefined) return <section class="panel"><p class="hint">Cargando…</p></section>;
  if (nombre === null) return (
    <section class="panel">
      <h2>Tu cuenta no tiene acceso</h2>
      <p>Este correo no está en el equipo de Talentino. Si crees que es un error, avísale a Felipe.</p>
    </section>
  );
  const s = SECCION[ruta.menu];
  const vista = () => {
    if (usaCfg && errCfg) return <section class="panel"><p class="error" role="alert">No se pudo leer la configuración: {errCfg}</p></section>;
    if (usaCfg && !cfg) return <section class="panel"><p class="hint">Cargando…</p></section>;
    switch (ruta.id) {
      case 'inicio': return <Inicio sb={sb} nombre={nombre} ir={ir} />;
      case 'productos': return <Productos sb={sb} ir={ir} onCambio={recargarCfg} />;
      case 'producto': return <ProductoDetalle key={ruta.args[0]} sb={sb} cfg={cfg!} ir={ir} sku={ruta.args[0]} onCambio={recargarCfg} />;
      case 'tablero': return <TableroOperacion sb={sb} cfg={cfg!} ir={ir} onTodo={contarTodo} />;
      case 'todo': return <Todo sb={sb} ir={ir} arg={ruta.args[0]} onCambio={contarTodo} />;
      case 'resultados': return <Resultados sb={sb} />;
      case 'gastos': return <Gastos sb={sb} cfg={cfg!} />;
      case 'inventario': return <Inventario sb={sb} tab={ruta.args[0]} />;
      case 'precios': return <Precios sb={sb} cfg={cfg!} ir={ir} onCambio={recargarCfg} />;
      case 'pedidos': return <Pedidos sb={sb} cfg={cfg!} ir={ir} />;
      case 'pedido': return <PedidoForm key={ruta.args.join('/')} sb={sb} cfg={cfg!} ir={ir} id={ruta.args[0] && ruta.args[0] !== 'nuevo' ? ruta.args[0] : null} clienteId={ruta.args[0] === 'nuevo' ? ruta.args[1] : undefined} />;
      case 'compartir': return <Compartir key={ruta.args.join('/')} sb={sb} cfg={cfg!} ir={ir} id={ruta.args[0]} tipo={ruta.args[1]} />;
      case 'clientes': return <Clientes sb={sb} cfg={cfg!} ir={ir} />;
      case 'ajustes': return <Ajustes sb={sb} cfg={cfg!} tab={ruta.args[0]} sub={ruta.args[1]} onCambio={recargarCfg} />;
      case 'campanas': return <Campanas sb={sb} cfg={cfg!} ir={ir} onCambio={recargarCfg} tab={ruta.args[0]} sel={ruta.args[1]} />;
      case 'mensaje': return ruta.args[0] === 'nueva' ? <MensajeNuevo sb={sb} cfg={cfg!} ir={ir} onCambio={recargarCfg} />
        : <MensajeDetalle key={ruta.args[0]} sb={sb} cfg={cfg!} ir={ir} id={ruta.args[0]} onCambio={recargarCfg} />;
      case 'campana': return <CampanaDetalle key={ruta.args[0]} sb={sb} cfg={cfg!} ir={ir} id={ruta.args[0]} onCambio={recargarCfg} />;
      case 'cliente': return <ClienteDetalle key={ruta.args[0]} sb={sb} cfg={cfg!} ir={ir} id={ruta.args[0]} />;
      default: return <Proximo s={s} />;
    }
  };
  return (
    <div class="sistema">
      <nav class="menu" aria-label="Menú">
        {MODULOS.map(m => (
          <div class="mod" key={m.nombre}>
            <div class="mod-t">{m.nombre}</div>
            {m.secciones.map(x => (
              <a key={x.id} href={'#/' + x.id} class={'nav' + (x.id === ruta.menu ? ' on' : '') + (x.proximo ? ' dim' : '')} aria-current={x.id === ruta.menu ? 'page' : undefined}>
                {x.nombre}{x.proximo && <span class="soon">próximo</span>}{x.id === 'todo' && todoN > 0 && <span class="insignia" aria-label={todoN + (todoN === 1 ? ' seguimiento por atender' : ' seguimientos por atender')}>{todoN}</span>}
              </a>
            ))}
          </div>
        ))}
      </nav>
      <div class="contenido">
        {vista()}
      </div>
      {objetivo.pregunta && <PreguntaObjetivo sb={sb} mes={objetivo.mes} onListo={objetivo.cerrar} />}
    </div>
  );
}
